import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

/**
 * Shared header for all admin sub-pages.
 * Renders: back arrow → icon → title + description
 *
 * @param {string}   title       - Page title
 * @param {string}   [description] - Optional subtitle text
 * @param {React.ElementType} [icon] - Optional lucide-react icon component
 * @param {string}   [iconColor]  - Tailwind text color class for the icon (e.g. 'text-blue-600')
 * @param {string}   [iconBg]     - Tailwind bg color class for the icon container (e.g. 'bg-blue-50')
 * @param {string}   [backTo]     - Navigate target (default: '/admin')
 * @param {React.ReactNode} [children] - Optional right-side content (buttons, etc.)
 */
export default function AdminPageHeader({ title, description, icon: Icon, iconColor = 'text-gray-500', iconBg = 'bg-gray-100', backTo = '/admin', children }) {
  const navigate = useNavigate();

  return (
    <div className="flex items-center justify-between mb-6">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(backTo)}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          title="Back to Admin"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        {Icon && (
          <div className={`h-10 w-10 rounded-xl ${iconBg} flex items-center justify-center`}>
            <Icon className={`h-5 w-5 ${iconColor}`} />
          </div>
        )}
        <div>
          <h1 className="text-xl font-bold text-gray-900">{title}</h1>
          {description && <p className="text-sm text-gray-500">{description}</p>}
        </div>
      </div>
      {children && <div className="flex items-center gap-2">{children}</div>}
    </div>
  );
}
